import api from './index';
// Phase 6 D-16: thin client wrapping every Phase 5 admin FAQ endpoint (Phase 5
// D-22). Mirrors the rallies.ts / tasks.ts conventions:
//  - import the shared PRIVATE_API axios instance from ./index (JWT + 401 retry);
//  - centralize all path roots in a single `endpoints` literal;
//  - named function exports only (no default export);
//  - return `res.data` for GET / POST / PATCH and `void` for DELETE;
//  - do NOT swallow errors — callers (06-03 / 06-04) branch on AxiosError
//    status codes for the 409 faq_category_not_empty and 400
//    item_category_mismatch cases (Phase 5 D-17).
//
// Public-side GET /_api/faq is owned by Phase 7 and intentionally lives outside
// this file.
//
// MUTATION-SHAPE NORMALIZATION (WR-faq-save-crash-undefined-de):
//   The Phase 5 PHP handlers for FAQ admin POST/PATCH return non-canonical
//   bodies (`{ uid }` on POST, `null` on PATCH) — they do NOT echo back the
//   full `AdminFaqItem` / `AdminFaqCategory` shape. Returning those stubs
//   directly to the edit forms made downstream row renders crash with
//   `Cannot read properties of undefined (reading 'de')` because the row
//   components index `item.translations[currentLang]` / `category.translations.de`.
//   We therefore re-fetch the canonical node via the existing GET-by-uid
//   endpoint after every mutation and resolve the wrapper promise with THAT.
//   The cost is one extra GET per save; the alternative (a backend handler
//   change) is tracked as a follow-up but requires a coordinated PHP +
//   serializer change.
const endpoints = {
    faqCategories: '/admin/faq/categories',
    faqCategorySorting: '/admin/faq/categories/sorting',
    faqItems: '/admin/faq/items',
};
// GET /_api/admin/faq/categories
// Phase 5 D-23: single call returns the full trilingual tree (categories with
// embedded items, all three languages). 06-03 fetches once on mount; 06-04
// reuses the same slice payload — no per-language refetch.
export const getAdminFaqCategories = async () => {
    const res = await api.get(endpoints.faqCategories);
    return res.data;
};
// GET /_api/admin/faq/categories/{uid}
export const getAdminFaqCategory = async (uid) => {
    const res = await api.get(`${endpoints.faqCategories}/${uid}`);
    return res.data;
};
// POST /_api/admin/faq/categories
// Backend returns `{ uid }`; we re-fetch the canonical node so the caller
// receives a full `AdminFaqCategory` (translations + items) and Redux state
// stays internally consistent with the GET-tree shape.
export const createFaqCategory = async (body) => {
    const res = await api.post(endpoints.faqCategories, body);
    const uid = res.data?.uid;
    if (typeof uid !== 'number') {
        // Defensive — the contract says POST returns `{ uid }`. If the server
        // ever changes shape (e.g. to echo the full resource), prefer that body.
        return res.data;
    }
    return getAdminFaqCategory(uid);
};
// PATCH /_api/admin/faq/categories/{uid}
// Backend returns 200 with `null`. Re-fetch by uid so the caller still receives
// a canonical `AdminFaqCategory`.
export const updateFaqCategory = async (uid, body) => {
    await api.patch(`${endpoints.faqCategories}/${uid}`, body);
    return getAdminFaqCategory(uid);
};
// DELETE /_api/admin/faq/categories/{uid}
// Phase 5 D-17: may return 409 `{ error: { code: 'faq_category_not_empty' } }`
// when the category still has items. The caller (06-03 modal) inspects
// `error.response.status === 409` on the AxiosError and surfaces the guided
// "delete or move items first" prompt. We deliberately do NOT catch here so
// the HTTP status flows through untouched.
export const deleteFaqCategory = async (uid) => {
    await api.delete(`${endpoints.faqCategories}/${uid}`);
};
// POST /_api/admin/faq/categories/sorting
export const sortFaqCategories = async (sorting) => {
    await api.post(endpoints.faqCategorySorting, { sorting });
};
// GET /_api/admin/faq/categories/{categoryId}/items
export const getAdminFaqItems = async (categoryId) => {
    const res = await api.get(`${endpoints.faqCategories}/${categoryId}/items`);
    return res.data;
};
// GET /_api/admin/faq/items/{uid}
// Returns the canonical per-item node (`{ uid, sorting, translations: { ... } }`).
// Used internally by `createFaqItem` / `updateFaqItem` to normalize the
// non-canonical POST/PATCH bodies into the full `AdminFaqItem` shape.
export const getAdminFaqItem = async (uid) => {
    const res = await api.get(`${endpoints.faqItems}/${uid}`);
    return res.data;
};
// POST /_api/admin/faq/categories/{categoryId}/items
// Backend returns `{ uid }`; we re-fetch the canonical node so the caller
// receives a full `AdminFaqItem` (translations) and Redux state stays
// internally consistent with the GET-tree shape.
export const createFaqItem = async (categoryId, body) => {
    const res = await api.post(`${endpoints.faqCategories}/${categoryId}/items`, body);
    const uid = res.data?.uid;
    if (typeof uid !== 'number') {
        // Defensive — see createFaqCategory for rationale.
        return res.data;
    }
    return getAdminFaqItem(uid);
};
// PATCH /_api/admin/faq/items/{uid}
// PATCH body may include `category: number` to reassign the item to a different
// parent category (D-19). Backend returns 200 with `null`; re-fetch by uid so
// the caller receives a canonical `AdminFaqItem`.
export const updateFaqItem = async (uid, body) => {
    await api.patch(`${endpoints.faqItems}/${uid}`, body);
    return getAdminFaqItem(uid);
};
// DELETE /_api/admin/faq/items/{uid}
export const deleteFaqItem = async (uid) => {
    await api.delete(`${endpoints.faqItems}/${uid}`);
};
// POST /_api/admin/faq/categories/{categoryId}/items/sorting
// Phase 5 D-17: may return 400 `{ error: { code: 'item_category_mismatch' } }`
// when one of the supplied UIDs does not belong to the named category. The
// caller branches on AxiosError status (400) to surface a recoverable message.
export const sortFaqItems = async (categoryId, sorting) => {
    await api.post(`${endpoints.faqCategories}/${categoryId}/items/sorting`, { sorting });
};
